// =============================================================================
// Login Page - Sarsen Capital Partners Investor Portal
// Design: Sarsen "Strata" identity (2026-09) via the shared AuthShell layout
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CodeInput } from '../../components/ui';
import { authService } from '../../services/auth.service';
import { useAuth } from '../../hooks/useAuth';
import { getApiError } from '../../services/api';
import {
  AuthShell,
  AuthHeading,
  AuthNotice,
  AuthError,
  AuthButton,
  AuthLink,
  authInputClass,
  authInputIconButtonClass,
  authQuietButtonClass,
} from '../../components/layout/AuthShell';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// =============================================================================
// Types
// =============================================================================

type Step = 'credentials' | 'email2fa' | 'sms2fa';

// =============================================================================
// Turnstile Site Key (Cloudflare CAPTCHA)
// =============================================================================

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

// =============================================================================
// Turnstile Widget Component
// =============================================================================

interface TurnstileProps {
  onVerify: (token: string) => void;
  onError?: () => void;
}

function TurnstileWidget({ onVerify, onError, resetKey }: TurnstileProps & { resetKey?: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);

  // Keep refs updated without triggering re-renders
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
  }, [onVerify, onError]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !containerRef.current) return;

    // Prevent multiple script loads
    const existingScript = document.querySelector('script[src*="turnstile"]');

    if (!window.turnstile && !existingScript) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      script.onload = () => renderWidget();
    } else if (window.turnstile) {
      renderWidget();
    } else if (existingScript) {
      // Script is loading, wait for it
      existingScript.addEventListener('load', () => renderWidget());
    }

    function renderWidget() {
      if (!containerRef.current || !window.turnstile) return;
      // Only render if not already rendered
      if (widgetIdRef.current) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => onVerifyRef.current(token),
        'error-callback': () => onErrorRef.current?.(),
        theme: 'light',
        size: 'normal',
      });
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Reset widget when resetKey changes (after failed login)
  useEffect(() => {
    if (resetKey && resetKey > 0 && widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (e) {
        // Widget may have been removed, re-render it
        console.warn('Turnstile reset failed, widget may need re-render');
      }
    }
  }, [resetKey]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={containerRef} />;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

// =============================================================================
// Component
// =============================================================================

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Check for session timeout message from redirect
  const sessionTimeoutMessage = location.state?.message as string | undefined;

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  // Ref to prevent duplicate verification requests
  const isVerifyingRef = useRef(false);

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await authService.login(email, password, turnstileToken);
      if (result.success && result.requiresEmail2fa) {
        setStep('email2fa');
        startCountdown();
      } else if (result.success && result.accessToken && result.refreshToken && result.user) {
        // Direct login (2FA bypassed)
        login(result.user, result.accessToken, result.refreshToken);
        navigate(result.user.role === 'ADMIN' || result.user.role === 'TEAM' ? '/admin' : '/dashboard');
      } else if (!result.success) {
        const errorMsg = result.error || 'Login failed';
        // If CAPTCHA failed, show a more helpful message
        if (errorMsg.toLowerCase().includes('captcha')) {
          setError('Please complete the security check below and try again.');
        } else {
          setError(errorMsg);
        }
        // Reset Turnstile widget on failure - tokens are single-use
        setTurnstileToken(null);
        setTurnstileResetKey((prev) => prev + 1);
      }
    } catch (err) {
      const apiError = getApiError(err).message;
      // If CAPTCHA failed, show a more helpful message
      if (apiError.toLowerCase().includes('captcha')) {
        setError('Please complete the security check below and try again.');
      } else {
        setError(apiError);
      }
      // Reset Turnstile widget on error - tokens are single-use
      setTurnstileToken(null);
      setTurnstileResetKey((prev) => prev + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmailCode = async (verificationCode: string) => {
    // Prevent duplicate requests (React StrictMode can cause double calls)
    if (isVerifyingRef.current) {
      return;
    }
    isVerifyingRef.current = true;

    setError(null);
    setIsLoading(true);

    try {
      const result = await authService.verifyEmailCode(email, verificationCode);
      if (result.success) {
        if (result.requiresSms2fa && result.sessionId) {
          setSessionId(result.sessionId);
          setStep('sms2fa');
          startCountdown();
        } else if (result.accessToken && result.refreshToken && result.user) {
          login(result.user, result.accessToken, result.refreshToken);
          navigate(result.user.role === 'ADMIN' || result.user.role === 'TEAM' ? '/admin' : '/dashboard');
        }
      } else {
        setError(result.error || 'Invalid code');
        setCode('');
        isVerifyingRef.current = false; // Allow retry on error
      }
    } catch (err) {
      setError(getApiError(err).message);
      setCode('');
      isVerifyingRef.current = false; // Allow retry on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySmsCode = async (smsVerificationCode: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await authService.verifySmsCode(smsVerificationCode, {
        sessionId: sessionId || undefined,
        purpose: 'login',
      });
      if (result.success && result.accessToken && result.refreshToken && result.user) {
        login(result.user, result.accessToken, result.refreshToken);
        navigate(result.user.role === 'ADMIN' || result.user.role === 'TEAM' ? '/admin' : '/dashboard');
      } else {
        setError(result.error || 'Invalid code');
        setSmsCode('');
      }
    } catch (err) {
      setError(getApiError(err).message);
      setSmsCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    setError(null);
    setIsLoading(true);

    try {
      if (step === 'email2fa') {
        await authService.login(email, password, null);
      } else if (step === 'sms2fa') {
        await authService.requestSmsCode('login');
      }
      startCountdown();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleBack = () => {
    if (step === 'email2fa') {
      setStep('credentials');
      setCode('');
      setPassword('');
    } else if (step === 'sms2fa') {
      setStep('email2fa');
      setSmsCode('');
    }
    setError(null);
  };

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <AuthShell caption="Investor Portal">
      {/* Step: Credentials */}
      {step === 'credentials' && (
        <div>
          <AuthHeading
            eyebrow="Investor Portal"
            title="Welcome back"
            subtitle={
              <span className="whitespace-nowrap text-[15px] sm:text-base">
                Sign in to access your Investor Portal.
              </span>
            }
          />

          {/* Session timeout message */}
          {sessionTimeoutMessage && <AuthNotice message={sessionTimeoutMessage} />}

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email Input */}
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
                autoComplete="email"
                autoFocus
                className={authInputClass}
              />
            </div>

            {/* Password Input */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                autoComplete="current-password"
                className={`${authInputClass} pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={authInputIconButtonClass}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Forgot Password Link */}
            <div>
              <AuthLink to="/forgot-password">Forgot password</AuthLink>
            </div>

            {/* Turnstile CAPTCHA */}
            <div className="pt-2">
              <TurnstileWidget
                onVerify={setTurnstileToken}
                onError={() => setTurnstileToken(null)}
                resetKey={turnstileResetKey}
              />
            </div>

            {/* Error Message */}
            {error && <AuthError message={error} />}

            {/* Submit Button */}
            <AuthButton
              type="submit"
              disabled={isLoading || (TURNSTILE_SITE_KEY ? !turnstileToken : false)}
            >
              {isLoading ? 'Signing in…' : 'Log in'}
            </AuthButton>
          </form>

          {/* First Time Access */}
          <div className="mt-10 pt-6 border-t border-brand-line">
            <p className="m-0 mb-1 text-sm text-brand-ink">First time accessing your portal?</p>
            <AuthLink to="/forgot-password?mode=activate" className="font-medium">
              Activate your account
            </AuthLink>
          </div>
        </div>
      )}

      {/* Step: Email 2FA */}
      {step === 'email2fa' && (
        <div>
          <AuthHeading
            eyebrow="Verification"
            title="Check your email"
            subtitle={
              <>
                We sent a 6-digit verification code to{' '}
                <span className="font-medium text-brand-navy">{email}</span>
              </>
            }
          />

          <div className="space-y-6">
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={handleVerifyEmailCode}
              error={!!error}
              disabled={isLoading}
            />

            {error && <AuthError message={error} center />}

            {isLoading && (
              <p className="m-0 text-sm text-brand-muted text-center">Verifying…</p>
            )}

            <div className="flex flex-col items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={countdown > 0 || isLoading}
                className={authQuietButtonClass}
              >
                {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
              </button>

              <button
                type="button"
                onClick={handleBack}
                className="text-sm text-brand-muted hover:text-brand-navy transition-colors"
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step: SMS 2FA */}
      {step === 'sms2fa' && (
        <div>
          <AuthHeading
            eyebrow="Verification"
            title="Two-factor authentication"
            subtitle="Enter the code sent to your phone."
          />

          <div className="space-y-6">
            <CodeInput
              value={smsCode}
              onChange={setSmsCode}
              onComplete={handleVerifySmsCode}
              error={!!error}
              disabled={isLoading}
            />

            {error && <AuthError message={error} center />}

            {isLoading && (
              <p className="m-0 text-sm text-brand-muted text-center">Verifying…</p>
            )}

            <div className="flex flex-col items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={countdown > 0 || isLoading}
                className={authQuietButtonClass}
              >
                {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend SMS code'}
              </button>

              <button
                type="button"
                onClick={handleBack}
                className="text-sm text-brand-muted hover:text-brand-navy transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthShell>
  );
}

export default LoginPage;
